const MIN_QUERY_LENGTH = 2;
const DEBOUNCE_MS = 180;

const escapeHtml = (value = '') => String(value)
  .replaceAll('&', '&amp;')
  .replaceAll('<', '&lt;')
  .replaceAll('>', '&gt;')
  .replaceAll('"', '&quot;')
  .replaceAll("'", '&#39;');

const normaliseRoot = (rootUrl = '/') => {
  const root = String(rootUrl || '/');
  return root.endsWith('/') ? root : `${root}/`;
};

export function buildPredictiveSearchUrl(rootUrl, query) {
  const params = new URLSearchParams();
  params.set('q', String(query || '').trim());
  params.set('resources[type]', 'product');
  params.set('resources[limit]', '6');
  params.set('resources[options][unavailable_products]', 'last');
  return `${normaliseRoot(rootUrl)}search/suggest.json?${params.toString()}`;
}

export function cleanPublicProductTitle(title) {
  return String(title || '').split(' · HE-')[0].trim() || String(title || '').trim();
}

export function formatStartingPrice(value, currency = 'USD', locale = 'en') {
  const amount = Number(value);
  if (!Number.isFinite(amount)) return '';
  return new Intl.NumberFormat(locale || 'en', {
    style: 'currency',
    currency: currency || 'USD',
    minimumFractionDigits: Number.isInteger(amount) ? 0 : 2,
    maximumFractionDigits: 2,
  }).format(amount);
}

function fullSearchUrl(searchUrl, query) {
  const params = new URLSearchParams({ q: query, type: 'product' });
  return `${searchUrl || '/search'}?${params.toString()}`;
}

function imageMarkup(product, title) {
  const image = product?.featured_image;
  const source = image?.url || product?.image || '';
  if (!source) return '<span class="listing-search__placeholder" aria-hidden="true">HE</span>';
  const width = Number(image?.width);
  const height = Number(image?.height);
  return `<img class="listing-search__image" src="${escapeHtml(source)}" alt="${escapeHtml(image?.alt || title)}" loading="lazy"${Number.isFinite(width) ? ` width="${width}"` : ''}${Number.isFinite(height) ? ` height="${height}"` : ''}>`;
}

export function renderPredictiveProducts(products, options = {}) {
  const query = String(options.query || '').trim();
  const viewAllUrl = fullSearchUrl(options.searchUrl, query).replaceAll('&', '&amp;');
  const rows = (products || []).map((product) => {
    const title = cleanPublicProductTitle(product?.title) || 'Untitled piece';
    const type = String(product?.type || 'Hewn Earth piece').trim();
    const price = formatStartingPrice(product?.price_min ?? product?.price, options.currency, options.locale);
    return `<a class="listing-search__result" role="option" href="${escapeHtml(product?.url || '#')}"><span class="listing-search__media">${imageMarkup(product, title)}</span><span class="listing-search__copy"><strong>${escapeHtml(title)}</strong><small>${escapeHtml(type)}</small></span>${price ? `<span class="listing-search__price">From ${escapeHtml(price)}</span>` : ''}</a>`;
  }).join('');
  const empty = rows ? '' : '<p class="listing-search__empty">No pieces found</p>';
  return `${rows}${empty}<a class="listing-search__all" href="${viewAllUrl}">View all results →</a>`;
}

export function createLatestSearchController({ load, onResolve, onReject = () => {} }) {
  let requestId = 0;
  return {
    cancel() { requestId += 1; },
    async search(query) {
      const currentId = ++requestId;
      try {
        const products = await load(query);
        if (currentId !== requestId) return false;
        onResolve(products, query);
        return true;
      } catch (error) {
        if (currentId !== requestId) return false;
        onReject(error);
        return false;
      }
    },
  };
}

async function fetchPredictiveProducts(root, query, fetchImpl = globalThis.fetch) {
  const response = await fetchImpl(buildPredictiveSearchUrl(root.dataset.searchRoot, query), {
    headers: { Accept: 'application/json' },
  });
  if (!response.ok) throw new Error(`Predictive search returned ${response.status}`);
  const payload = await response.json();
  return payload?.resources?.results?.products || [];
}

const searchDisposers = new WeakMap();
export function disposeCollectionSearch(root) {
  searchDisposers.get(root)?.();
  searchDisposers.delete(root);
  delete root.dataset.searchBound;
}

export function initialiseCollectionSearch(root, options = {}) {
  if (!root || root.dataset.searchBound === 'true') return;
  const input = root.querySelector('[data-collection-search-input]');
  const results = root.querySelector('[data-collection-search-results]');
  if (!input || !results) return;

  root.dataset.searchBound = 'true';
  let timer;
  const close = () => {
    results.hidden = true;
    input.setAttribute('aria-expanded', 'false');
  };
  const open = (products, query) => {
    results.innerHTML = renderPredictiveProducts(products, {
      query,
      locale: root.dataset.searchLocale,
      currency: root.dataset.searchCurrency,
      searchUrl: root.dataset.searchUrl,
    });
    results.hidden = false;
    input.setAttribute('aria-expanded', 'true');
  };
  const controller = createLatestSearchController({
    load: (query) => fetchPredictiveProducts(root, query, options.fetchImpl),
    onResolve: open,
    onReject: close,
  });

  input.addEventListener('input', () => {
    clearTimeout(timer);
    const query = input.value.trim();
    if (query.length < MIN_QUERY_LENGTH) {
      controller.cancel();
      results.innerHTML = '';
      close();
      return;
    }
    timer = setTimeout(() => controller.search(query), DEBOUNCE_MS);
  });

  input.addEventListener('keydown', (event) => {
    if (event.key === 'Escape') {
      controller.cancel();
      close();
      return;
    }
    if (event.key === 'ArrowDown' && !results.hidden) {
      const firstResult = results.querySelector('[role="option"]');
      if (firstResult) {
        event.preventDefault();
        firstResult.focus();
      }
    }
  });

  results.addEventListener('keydown', (event) => {
    const optionsInPanel = [...results.querySelectorAll('[role="option"]')];
    const index = optionsInPanel.indexOf(event.target);
    if (event.key === 'Escape') {
      close();
      input.focus();
    } else if (event.key === 'ArrowDown' && index > -1) {
      event.preventDefault();
      optionsInPanel[Math.min(index + 1, optionsInPanel.length - 1)]?.focus();
    } else if (event.key === 'ArrowUp' && index > -1) {
      event.preventDefault();
      if (index === 0) input.focus();
      else optionsInPanel[index - 1]?.focus();
    }
  });

  const outsideClick = event => { if (!root.contains(event.target)) close(); };
  document.addEventListener('click', outsideClick);
  searchDisposers.set(root, () => { clearTimeout(timer); controller.cancel(); document.removeEventListener('click', outsideClick); });
}

function initialiseAllCollectionSearches() {
  document.querySelectorAll('[data-collection-search]').forEach((root) => initialiseCollectionSearch(root));
}

if (typeof document !== 'undefined') {
  window.HewnCollectionSearch = {initialiseCollectionSearch, disposeCollectionSearch};
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', initialiseAllCollectionSearches);
  else initialiseAllCollectionSearches();
}
